import React, { useState, useEffect } from 'react';
import {
  Search,
  User,
  Calendar,
  MapPin,
  Package,
  Clock,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import {
  ApprovalRequest,
  getPendingApprovals,
  getApprovalStats,
  ApprovalStats,
} from '../../../services/approvalService';
import ProjectApprovalDrawer from '../components/ProjectApprovalDrawer';
import { format } from 'date-fns';

const ProjectApprovalDashboard: React.FC = () => {
  const [pendingApprovals, setPendingApprovals] = useState<ApprovalRequest[]>([]);
  const [stats, setStats] = useState<ApprovalStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const { user } = useAuth();

  useEffect(() => {
    loadPendingApprovals();
    loadStats();
  }, []);

  const loadPendingApprovals = async () => {
    try {
      setLoading(true);
      const response = await getPendingApprovals({ page: 1, limit: 50 });
      const projectApprovals = (response.data || []).filter(
        (req: ApprovalRequest) => req.resourceType === 'project'
      );
      setPendingApprovals(projectApprovals);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      toast.error(e.response?.data?.message || 'Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await getApprovalStats();
      if (res?.data) setStats(res.data);
    } catch {
      // non-critical
    }
  };

  const handleViewProject = (projectId: string) => {
    setSelectedProjectId(projectId);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedProjectId(null);
    loadPendingApprovals();
    loadStats();
  };

  const filteredProjects = pendingApprovals.filter((project) => {
    const matchSearch =
      !searchQuery ||
      project.requestData?.projectName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.requestData?.projectId?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchStatus = statusFilter === 'all' || project.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const totalProjects = pendingApprovals.length;
  const pendingReview = pendingApprovals.filter((p) => p.status === 'pending').length;
  const scheduled = pendingApprovals.filter((p) => p.requestData?.startDate).length;
  const nonScheduled = totalProjects - scheduled;

  const getStatusSeverity = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s === 'pending' || s === 'pending_approval') return 'warning';
    if (s === 'approved' || s === 'scheduled') return 'success';
    if (s === 'rejected') return 'danger';
    if (s === 'draft') return 'secondary';
    return 'info';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 font-[family-name:var(--font-outfit)]">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 tracking-tight">
            Project Approval Dashboard
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Review and manage project submissions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-600 hidden sm:inline">{user?.name}</span>
          <Button
            icon="pi pi-refresh"
            label="Refresh"
            className="p-button-outlined p-button-sm"
            onClick={() => {
              loadPendingApprovals();
              loadStats();
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Card className="!shadow-sm !border !border-gray-200 !rounded-xl overflow-hidden">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Total</p>
              <p className="text-lg font-semibold text-gray-900 tabular-nums">{totalProjects}</p>
            </div>
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
              <Package className="w-4 h-4 text-blue-600" />
            </div>
          </div>
        </Card>
        <Card className="!shadow-sm !border !border-gray-200 !rounded-xl overflow-hidden">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Pending</p>
              <p className="text-lg font-semibold text-gray-900 tabular-nums">{pendingReview}</p>
            </div>
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
              <Clock className="w-4 h-4 text-amber-600" />
            </div>
          </div>
        </Card>
        <Card className="!shadow-sm !border !border-gray-200 !rounded-xl overflow-hidden">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Scheduled</p>
              <p className="text-lg font-semibold text-gray-900 tabular-nums">{scheduled}</p>
            </div>
            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
            </div>
          </div>
        </Card>
        <Card className="!shadow-sm !border !border-gray-200 !rounded-xl overflow-hidden">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Non-scheduled</p>
              <p className="text-lg font-semibold text-gray-900 tabular-nums">{nonScheduled}</p>
            </div>
            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
              <AlertCircle className="w-4 h-4 text-gray-600" />
            </div>
          </div>
        </Card>
      </div>

      <Card className="!shadow-sm !border !border-gray-200 !rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Project submissions</h3>
            <p className="text-[11px] text-gray-500 mt-0.5">Manage and review all projects</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-full sm:w-44">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search projects…"
                className="w-full text-xs border border-gray-300 rounded-lg py-1.5 pl-8 pr-2 focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-xs border border-gray-300 rounded-lg py-1.5 px-2.5 focus:ring-1 focus:ring-gray-400"
            >
              <option value="all">All status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex justify-center items-center py-16">
              <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-600 rounded-full animate-spin" />
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-16">
              <Package className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-xs text-gray-600">No projects found</p>
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-2 text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Project</th>
                  <th className="px-3 py-2 text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Category</th>
                  <th className="px-3 py-2 text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Location</th>
                  <th className="px-3 py-2 text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Requester</th>
                  <th className="px-3 py-2 text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-3 py-2 text-right text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredProjects.map((project) => (
                  <tr key={project._id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-3 py-2">
                      <div>
                        <div className="text-xs font-medium text-gray-900">
                          {project.requestData?.projectName || 'Unnamed'}
                        </div>
                        <div className="text-[10px] text-gray-500 mt-0.5">
                          {project.requestData?.totalAssets ?? 0} assets · {project.requestData?.totalEquipment ?? 0} equip
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <Tag
                        value={project.requestData?.category || 'General'}
                        severity="info"
                        className="!text-[10px] !px-2 !py-0.5"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1 text-[11px] text-gray-600">
                        <MapPin className="w-3 h-3 text-gray-400" />
                        {(project.requestData?.location as string) || '—'}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                          <User className="w-3 h-3 text-gray-500" />
                        </div>
                        <div>
                          <div className="text-[11px] font-medium text-gray-900">{project.requesterName}</div>
                          <div className="text-[10px] text-gray-500">
                            {format(new Date(project.createdAt), 'MMM d')}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <Tag
                        value={(project.status || '').replace('_', ' ')}
                        severity={getStatusSeverity(project.status)}
                        className="!text-[10px] !px-2 !py-0.5"
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        icon="pi pi-eye"
                        label="View"
                        className="p-button-text p-button-sm"
                        onClick={() => project.resourceId && handleViewProject(project.resourceId)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {selectedProjectId && (
        <ProjectApprovalDrawer
          projectId={selectedProjectId}
          open={drawerOpen}
          onClose={handleCloseDrawer}
        />
      )}
    </div>
  );
};

export default ProjectApprovalDashboard;
